import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { BrandSelect, BrandSelectOption, DAPLogo } from '@dap/common/ui';
import { AppBar, Box, Divider, SwipeableDrawer, IconButton, Toolbar, Stack } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { UserData } from '@dap/memberadmin/types';
import { useAppSelector, useBrandStyle, useGetBrandQuery } from '@dap/common/data-access';
import { getSettingsRoute } from '@dap/common/consts';
import { getUserAppsForBrand } from '@dap/memberadmin/utils';
import { AppType } from '@dap/common/types';
import {
  KeyboardArrowRight,
  MenuRounded,
  CloseRounded,
  SettingsOutlined,
} from '@mui/icons-material';
import {
  App,
  Navigation,
  NavRoute,
  SidebarAppMenu,
  SidebarHeader,
  SidebarUserMenu,
} from '@shared/components';
import { appLogoIcons } from '@assets/appLogos';
import { useNavRoutes } from '../../Sidebar/hooks/useNavRoutes';
import { useSubjectRoutes } from '../../Sidebar/hooks/useSubjectRoutes';

export interface MobileProps {
  userData: UserData;
  brands: BrandSelectOption[];
  selectedBrandKey: string;
  onSelectBrand: (brandKey: string) => void;
  onLogout: () => void;
}

const headerHeight = 60; // Adjust this value based on your design

export function Mobile({
  userData,
  brands,
  selectedBrandKey,
  onSelectBrand,
  onLogout,
}: MobileProps) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  const { brandApps } = useGetBrandQuery(selectedBrandKey, {
    selectFromResult: ({ data }) => ({
      brandApps: data?.apps ?? [],
    }),
  });

  const brandStyle = useBrandStyle(selectedBrandKey);

  const userApps = useMemo(() => {
    const availableApps = getUserAppsForBrand(brandApps, userData.apps)?.filter(
      (app) => app.key !== AppType.Memberadmin
    );

    return availableApps.map((app) => {
      return {
        key: app.key,
        name: app.name,
        url: app?.url ?? '',
        default: false,
        Icon: appLogoIcons[app.key] ?? KeyboardArrowRight,
      } as App;
    });
  }, [brandApps, userData]);

  const userPicture = useAppSelector((state) => state.user.userPicture);
  const navRoutes = useNavRoutes();
  const subjectRoutes = useSubjectRoutes();
  // Kept separate from useNavRoutes(), which also feeds the desktop Sidebar - a settings
  // entry there would duplicate the desktop header's own settings icon.
  const settingsNavRoute: NavRoute = {
    title: t('settings.title'),
    Icon: SettingsOutlined,
    to: getSettingsRoute(),
    isActive: pathname === getSettingsRoute(),
  };

  const username = useMemo(
    () =>
      userData && userData.name ? userData.name : `${userData?.givenName} ${userData?.familyName}`,
    [userData]
  );

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <>
      <AppBar position="fixed" sx={{ height: 'auto' }}>
        <Toolbar
          sx={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr auto',
            justifyContent: 'space-between',
            alignItems: 'center',
            minHeight: headerHeight,
          }}
        >
          <IconButton
            sx={{ padding: 0, gridColumn: '1', gridRow: '1' }}
            onClick={() => {
              navigate('/');
            }}
          >
            <img
              src={brandStyle.icon}
              alt=""
              height="40px"
              width="40px"
              style={{ display: 'block', objectFit: 'contain' }}
            />
          </IconButton>
          <DAPLogo
            sx={{
              position: 'relative',
              zIndex: '-1',
              gridColumn: '1 / -1',
              gridRow: '1',
              justifySelf: 'center',
            }}
          />
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ gridColumn: '3', gridRow: '1' }}
          >
            <IconButton
              aria-label={open ? t('menu.close') : t('menu.open')}
              sx={{ color: 'grey.500' }}
              onClick={() => {
                setOpen((open) => !open);
              }}
            >
              {open ? (
                <CloseRounded sx={{ fontSize: 28 }} />
              ) : (
                <MenuRounded sx={{ fontSize: 28 }} />
              )}
            </IconButton>
          </Stack>
        </Toolbar>
      </AppBar>

      <SwipeableDrawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        onOpen={() => setOpen(true)}
        sx={{
          top: headerHeight,
          '& .MuiDrawer-paper': {
            top: headerHeight,
            height: 'auto',
            maxHeight: `calc(100dvh - ${headerHeight}px)`, // 64px is the height of the AppBar
            overflowY: 'auto',
            borderRadius: '0px 0px 0px 8px',
          },
          '& .MuiModal-backdrop': {
            top: headerHeight,
            backdropFilter: 'blur(2px)',
          },
        }}
      >
        <Box sx={{ padding: 3 }}>
          <BrandSelect
            brands={brands}
            selectedBrandKey={selectedBrandKey}
            selectedTitle="Digital Arbeidsplass"
            onBrandSelect={onSelectBrand}
          />
        </Box>
        <Divider orientation="horizontal" sx={{ marginInline: 3 }} />
        <Navigation navRoutes={navRoutes} expandedView={open} expandView={() => setOpen(true)} />
        {subjectRoutes.length > 0 && (
          <>
            {/* Subjects enabled for brand */}
            {!open && <Divider orientation="horizontal" sx={{ marginInline: 3 }} />}
            {userApps.length > 0 && (
              <SidebarHeader title={t('subjects.title')} expandedView={open} />
            )}
            <Navigation
              navRoutes={subjectRoutes}
              expandedView={open}
              expandView={() => setOpen(true)}
            />
          </>
        )}
        {/* Apps enabled for user */}
        <Divider orientation="horizontal" sx={{ marginInline: 3 }} />
        {userApps.length > 0 && (
          <SidebarHeader title={t('sidebar.apps.title')} expandedView={open} />
        )}
        <SidebarAppMenu accessibleApps={userApps} visibleApps={3} expandedView={open} />
        <Divider orientation="horizontal" sx={{ marginInline: 3 }} />
        <Navigation
          navRoutes={[settingsNavRoute]}
          expandedView={open}
          expandView={() => setOpen(true)}
        />
        <Box
          marginTop="auto"
          display="flex"
          justifyContent="center"
          p={3}
          id="usermenu"
          sx={{
            '& .MuiStack-root .MuiStack-root': {
              flex: 1,
            },
            '& .MuiBox-root+.MuiBox-root': {
              flex: 1,
              containerType: 'inline-size',
            },
            '& .MuiTypography-root': {
              textOverflow: 'ellipsis',
              overflow: 'hidden',
            },
          }}
        >
          <SidebarUserMenu
            userName={username}
            jobTitle={userData?.jobTitle ?? ''}
            userPicture={userPicture}
            logOut={onLogout}
            expandedView={open}
          />
        </Box>
      </SwipeableDrawer>
    </>
  );
}
export default Mobile;
